// Quality data layer (Module 07 §5, slice 07-A). TanStack Query hooks over the
// generated api-client — doctrine 3: apps/web talks to the API only through
// `@assessly/api-client`, never a hand-rolled fetch. Every mutation sends a
// fresh `Idempotency-Key` (§5: "Idempotency-Key required on every mutation")
// and invalidates the lists it affects.
//
// TWO PAGINATION SHAPES, because §5 has two and they mean different things:
//   * the CATALOGS (control points, failure causes) are UNPAGINATED by contract
//     — bounded definition sets the floor PWA must hold WHOLE to execute an
//     inspection offline. There is no cursor plumbing here because a page-walk
//     would be inventing a control the server does not offer, and a paged
//     catalog is a device that has "some of the checks".
//   * the QUEUE is cursor-paginated and genuinely unbounded, so it folds pages
//     the way module 05's ledger does (lib/paged-rows.ts).
//
// THE EXECUTION WRITES ARE NOT HERE. `POST /results` and `POST /complete` go
// through the FLOOR OUTBOX on both shells — including the office fallback —
// because §4.4 says the office screen is identical and because a mutation that
// takes one path online and another offline is the branch that rots (the rule
// floor-outbox.ts opens with). See `components/inspection-run.tsx`.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];

export type QualityControlPoint = S["QualityControlPoint"];
export type QualityCheckItem = S["QualityCheckItem"];
export type QualityControlPointBinding = S["QualityControlPointBinding"];
export type QualityControlPointCreateRequest = S["QualityControlPointCreateRequest"];
export type QualityCheckItemInput = S["QualityCheckItemInput"];
export type QualityFailureCause = S["QualityFailureCause"];
export type QualityInspection = S["QualityInspection"];
export type QualityInspectionDetail = S["QualityInspectionDetailResponse"];
export type QualityInspectionResult = S["QualityInspectionResult"];
export type QualityStage = S["QualityControlPoint"]["stage"];
export type QualitySampleMode = S["QualityControlPoint"]["sample_mode"];
export type QualityCheckType = S["QualityCheckItem"]["check_type"];
export type QualityInspectionStatus = S["QualityInspection"]["status"];

// 07-B's quarantine surface (§2.7 / §4.6). The wire says `hold` — renaming a
// path is an oasdiff break — and every rendered string says QUARANTINE
// (D-123 ruling 9), which is why the hooks below are named for the word the
// user reads while the types keep the contract's spelling.
export type QualityHold = S["QualityHold"];
export type QualityHoldStatus = S["QualityHold"]["status"];
export type QualityHoldDispositionRequest = S["QualityHoldDispositionRequest"];
export type QualityHoldPlaceRequest = S["QualityHoldPlaceRequest"];
export type QualityResolveRequest = S["QualityInspectionResolveRequest"];
export type QualityResolutionResponse = S["QualityResolutionResponse"];
/** §3.5a's rework-door PREVIEW for a chosen target (D-299 owner ruling R8):
 * `GET /v1/quality/inspections/{id}/rework-target`. */
export type QualityReworkTarget = S["QualityReworkTargetResponse"];

// 07-C's guided cut (§2.8 / §2.9 / §3.5d). The ELIGIBILITY BLOCK rides the
// inspection detail (D-158) and is the ONLY thing the §4.4 fifth button and the
// per-item fault-position input read: a client-side re-derivation of this
// judgement is how the server's own two copies drifted for a whole slice
// (D-181), and this client is not going to be the third.
export type QualityInspectionGuidedCut = S["QualityInspectionGuidedCut"];
export type QualityGuidedCutReason = NonNullable<QualityInspectionGuidedCut["reason"]>;
export type QualityCutEvent = S["QualityCutEvent"];
export type QualityCutSegment = S["QualityCutSegment"];
export type QualityCutEventDetail = S["QualityCutEventDetailResponse"];
export type QualityCutExecuteResponse = S["QualityCutExecuteResponse"];

/**
 * THE `guided_cut` BLOCK, READ HONESTLY — the one place the contract's type and
 * the runtime disagree, and the disagreement is stated here rather than papered
 * over at five call sites.
 *
 * The contract makes `guided_cut` REQUIRED on the inspection detail, and the
 * server always sends it. The floor does not always read the server: its
 * per-inspection read is a `useCachedRead` persisted under
 * `assessly.floor.read.v1:` — a key carrying NO schema version — so a device
 * that opened an inspection BEFORE 07-C shipped holds a snapshot of the OLD
 * response shape and will keep rendering from it, offline, for as long as it
 * stays offline. Dereferencing `.eligible` on that snapshot is a TypeError, and
 * a TypeError inside a floor screen is a white page at the drum, which is the
 * one failure mode the offline half of this module exists to prevent.
 *
 * So the read goes through here: a widening cast, once, with the reason beside
 * it, producing an `| undefined` the type system then propagates honestly.
 * Callers render its absence as a stated fact ("this device cannot tell whether
 * a cut is offered here — reconnect and open the check again"), never as a
 * silent absence and never as an eligible cut.
 *
 * **Delete this the day the floor read cache gains a schema version** and a
 * stale-shape entry is dropped rather than rendered.
 */
export function guidedCutOf(
  detail: QualityInspectionDetail | undefined,
): QualityInspectionGuidedCut | undefined {
  return (detail as { guided_cut?: QualityInspectionGuidedCut } | undefined)?.guided_cut;
}

/** Module 04's §3.1 status of the order a `rework` with no explicit ref would
 * re-open (D-299 ruling G5) — the non-null half of the inspection read's
 * `rework_target_work_order_status`. */
export type QualityReworkTargetWorkOrderStatus = NonNullable<
  QualityInspectionDetail["rework_target_work_order_status"]
>;

/** The six §3.1 values the read may carry, for the shape check below. */
const REWORK_TARGET_STATUSES = [
  "draft",
  "released",
  "in_progress",
  "completed",
  "closed",
  "cancelled",
] as const satisfies readonly QualityReworkTargetWorkOrderStatus[];

/**
 * `rework_target_work_order_status`, READ THE SAME HONEST WAY AS `guided_cut`
 * and for the same reason (see `guidedCutOf` above): the contract makes it
 * required, and a floor device's versionless cached snapshot from before
 * D-299 does not carry it. Three answers, kept apart:
 *
 *  - a status — the server named the order the rework would re-open;
 *  - `null` — the server said there is no known target (the inspection names
 *    no operation, so a rework needs an explicit ref the read cannot know);
 *  - `undefined` — this payload predates the field.
 *
 * Only the first can render Rework dead: `null` and `undefined` both render it
 * live and let §3.5a's refusal be relayed (spec 07 §4.4's D-299 note). A value
 * outside the enum (a newer server widening it) reads as `undefined` rather
 * than as a status this client would mis-spell.
 */
export function reworkTargetWorkOrderStatusOf(
  detail: QualityInspectionDetail | undefined,
): QualityReworkTargetWorkOrderStatus | null | undefined {
  const raw = (detail as { rework_target_work_order_status?: unknown } | undefined)
    ?.rework_target_work_order_status;
  if (raw === null) return null;
  return typeof raw === "string" && (REWORK_TARGET_STATUSES as readonly string[]).includes(raw)
    ? (raw as QualityReworkTargetWorkOrderStatus)
    : undefined;
}

/** Whether §3.5a would refuse a rework on this target's order
 * (`blocked_by: work_order_not_live`): a KNOWN status that is not `released` or
 * `in_progress`. Module 04's rule, restated here only as a rendering hint — the
 * refusal stays the authority (D-299 ruling G5). */
export function reworkTargetOrderNotLive(
  status: QualityReworkTargetWorkOrderStatus | null | undefined,
): status is Exclude<QualityReworkTargetWorkOrderStatus, "released" | "in_progress"> {
  return (
    status !== null && status !== undefined && status !== "released" && status !== "in_progress"
  );
}

export type ControlPointsQuery = NonNullable<
  paths["/v1/quality/control-points"]["get"]["parameters"]["query"]
>;
export type InspectionsQuery = NonNullable<
  paths["/v1/quality/inspections"]["get"]["parameters"]["query"]
>;
export type QualityHoldsQuery = NonNullable<
  paths["/v1/quality/holds"]["get"]["parameters"]["query"]
>;

export const qualityKeys = {
  all: ["quality"] as const,
  controlPoints: (query: ControlPointsQuery) => ["quality", "control-points", query] as const,
  controlPointLists: () => ["quality", "control-points"] as const,
  controlPoint: (id: string) => ["quality", "control-point", id] as const,
  failureCauses: () => ["quality", "failure-causes"] as const,
  inspections: (query: InspectionsQuery, cursor: string | undefined) =>
    ["quality", "inspections", query, cursor ?? null] as const,
  inspectionLists: () => ["quality", "inspections"] as const,
  inspection: (id: string) => ["quality", "inspection", id] as const,
  /** §3.5a's rework-door preview for one target (`null` = the door's own
   * default, the inspection's own step) — D-299 R8 / owner ruling Q4. Under
   * the inspection's own key, so any invalidation of the inspection re-asks. */
  reworkTarget: (
    id: string,
    workOrderOperationId: string | null,
    door: "inspection_resolve" | "hold_disposition",
  ) => ["quality", "inspection", id, "rework-target", workOrderOperationId, door] as const,
  holds: (query: QualityHoldsQuery, cursor: string | undefined) =>
    ["quality", "holds", query, cursor ?? null] as const,
  holdLists: () => ["quality", "holds"] as const,
  filterProducts: () => ["quality", "filter-products"] as const,
  coaTemplates: () => ["quality", "coa-templates"] as const,
  analyticsFpy: (q: object) => ["quality", "analytics", "fpy", q] as const,
  analyticsPareto: (q: object) => ["quality", "analytics", "pareto", q] as const,
  analyticsScoreboard: (q: object) => ["quality", "analytics", "scoreboard", q] as const,
  analyticsSuppliers: (q: object) => ["quality", "analytics", "suppliers", q] as const,
  coaDocuments: (query: CoaDocumentsQuery) => ["quality", "coa-documents", query] as const,
  coaDocumentLists: () => ["quality", "coa-documents"] as const,
};

export interface CoaDocumentsQuery {
  status?: "draft" | "issued" | "revoked" | undefined;
  lotId?: string | undefined;
}

// ---------------------------------------------------------------------------
// §4.1 / §4.2 — the control-point library and its editor
// ---------------------------------------------------------------------------

export function useQualityControlPoints(query: ControlPointsQuery, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.controlPoints(query),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/control-points", { signal, params: { query } });
      return unwrap(res, "Could not load the control points").items;
    },
  });
}

export function useQualityControlPoint(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.controlPoint(id ?? ""),
    enabled: enabled && id !== undefined && id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/control-points/{id}", {
        signal,
        params: { path: { id: id ?? "" } },
      });
      return unwrap(res, "Could not load the control point");
    },
  });
}

export function useCreateQualityControlPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QualityControlPointCreateRequest) => {
      const res = await client.POST("/v1/quality/control-points", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the control point");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qualityKeys.controlPointLists() }),
  });
}

/**
 * §3.1 step 5 — an EDIT is a new version, never a mutation of a definition an
 * inspection may have pinned. The server creates version N+1 and flips the old
 * row `is_current = false` in the same transaction; the client's whole part in
 * that is to POST here and re-read. There is no PUT, and the screen says so
 * with the version banner rather than pretending it is saving in place.
 */
export function useVersionQualityControlPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: QualityControlPointCreateRequest }) => {
      const res = await client.POST("/v1/quality/control-points/{id}/versions", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the new version");
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: qualityKeys.controlPointLists() });
      void qc.invalidateQueries({ queryKey: qualityKeys.controlPoint(input.id) });
    },
  });
}

export function useDeactivateQualityControlPoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/quality/control-points/{id}/deactivate", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not deactivate the control point");
    },
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: qualityKeys.controlPointLists() });
      void qc.invalidateQueries({ queryKey: qualityKeys.controlPoint(id) });
    },
  });
}

/**
 * §3.1's pack seeding — the §4.1 empty state's second CTA. Idempotent per code
 * (§9.15): a row the tenant already has is COUNTED as skipped and never
 * overwritten, which is why the response reports both halves and the screen
 * reports both back.
 */
export function useSeedQualityVerticalPack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pack: S["QualityVerticalPackSeedRequest"]["pack"]) => {
      const res = await client.POST("/v1/quality/vertical-pack/seed", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { pack },
      });
      return unwrap(res, "Could not seed the pack");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qualityKeys.controlPointLists() });
      void qc.invalidateQueries({ queryKey: qualityKeys.failureCauses() });
    },
  });
}

// ---------------------------------------------------------------------------
// §2.4 — the failure-cause catalog
// ---------------------------------------------------------------------------

/**
 * Readable by ANY authenticated principal and carrying NO permission code
 * (§5 row 7), which is why this hook takes no `enabled` gate on a mirror: the
 * §4.4 cause picker is driven by floor workers and storekeepers whose §7 read
 * cells are "—", and a cause they cannot list is a completion they cannot
 * finish.
 */
export function useQualityFailureCauses(enabled = true) {
  return useQuery({
    queryKey: qualityKeys.failureCauses(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/failure-causes", {
        signal,
        params: { query: { active: "true" } },
      });
      return unwrap(res, "Could not load the failure causes").items;
    },
  });
}

/**
 * §5 row 8a — MAP an existing failure cause onto a module-05 waste cause
 * (fix #117; the D-132 pt 5 remedy).
 *
 * A PUT on a sub-resource, not a cause update: the row's `code`, `category` and
 * `name_i18n` have no client door at all, here or on the server, and the one
 * writable column has a path of its own. Invalidates the cause list because
 * that is the read the §4.1b screen and the §4.4 picker both draw from.
 */
export function useMapFailureCauseWasteCause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; wasteCauseCode: string }) => {
      const res = await client.PUT("/v1/quality/failure-causes/{id}/waste-cause", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { waste_cause_code: input.wasteCauseCode },
      });
      return unwrap(res, "Could not map the failure cause");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qualityKeys.failureCauses() }),
  });
}

/**
 * MODULE 05's ACTIVE WASTE CAUSES — the options the mapping control offers.
 *
 * `GET /v1/waste/categories?include=causes&is_active=true`, and NOT a new
 * `GET /v1/waste/causes` (fix #117, argued rather than reflexed): this read
 * already serves the WHOLE active cause catalog — `include=causes` is not
 * scoped to the categories in the response — it carries no permission code
 * (any authenticated principal, module 05 §5 row 1), and three surfaces
 * already draw from it (the floor waste capture grid, the office waste
 * settings editor, module 04's scrap-reason picker). A second route onto the
 * same rows would be a second door with no capability behind it, and the
 * refusal message this fix rewrote now names THIS read — so the sentence and
 * the screen ask the server the same question.
 *
 * A cross-module read through the one API (doctrine 3), the same shape §4.2's
 * binding pickers use for module 01's catalogue.
 */
export function useWasteCauseOptions(enabled: boolean) {
  return useQuery({
    queryKey: ["quality", "waste-cause-options"] as const,
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/categories", {
        signal,
        params: { query: { include: "causes", is_active: "true" } },
      });
      return unwrap(res, "Could not load the waste causes").causes ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// §4.3 — the inspection queue
// ---------------------------------------------------------------------------

export function useQualityInspections(
  query: InspectionsQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: qualityKeys.inspections(query, cursor),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/inspections", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the inspection queue");
    },
  });
}

/**
 * §5 — the detail carries the check items OF THE PINNED VERSION, not the
 * current one. A client that fetched the current definition instead would
 * render the wrong bounds the moment somebody tightens a limit (M07-S12), and
 * that is a wrong VERDICT rather than a stale screen. Nothing in this area ever
 * grades against `useQualityControlPoint`.
 */
export function useQualityInspection(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.inspection(id ?? ""),
    enabled: enabled && id !== undefined && id.length > 0,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/inspections/{id}", {
        signal,
        params: { path: { id: id ?? "" } },
      });
      return unwrap(res, "Could not load the inspection");
    },
  });
}

/**
 * `GET /v1/quality/inspections/{id}/rework-target` for the §4.6 quarantine
 * dialog (D-299 owner ruling Q4) — the same read §4.4's sheet asks through its
 * arm port, answering for exactly the step the rework door would re-open and
 * whether THIS caller may send the rework through `door` (round 4: the read is
 * principal-aware at the door it is asked at, and the dialog's rework goes
 * through the quarantine door, `hold_disposition`, whose authority is not the
 * sheet's). `null` sends no ref, so the door's own default (the inspection's
 * own step) answers.
 *
 * A read that is ADVISORY and moves with the floor, so it is never cached
 * across a decision (`staleTime: 0`) and never retried: a 422 for an unknown
 * step is an answer, not a transient, and a transport failure is "no
 * verdict", which leaves the dialog's Confirm to the door.
 */
export function useQualityReworkTarget(
  inspectionId: string | undefined,
  workOrderOperationId: string | null,
  door: "inspection_resolve" | "hold_disposition",
  enabled: boolean,
) {
  return useQuery({
    queryKey: qualityKeys.reworkTarget(inspectionId ?? "", workOrderOperationId, door),
    enabled: enabled && inspectionId !== undefined && inspectionId.length > 0,
    staleTime: 0,
    retry: false,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/inspections/{id}/rework-target", {
        signal,
        params: {
          path: { id: inspectionId ?? "" },
          query:
            workOrderOperationId === null
              ? { door }
              : { work_order_operation_id: workOrderOperationId, door },
        },
      });
      return unwrap(res, "Could not check that step");
    },
  });
}

// ---------------------------------------------------------------------------
// §4.2's binding PICKERS — module 01's catalogue, read across the boundary
// ---------------------------------------------------------------------------

/**
 * The products a §2.2 binding may name, for the editor's pickers.
 *
 * §4.2 asks for "bindings repeater (PICKERS per stage rules §2.2)", and the
 * word is load-bearing: a binding is a uuid on the wire, and a control that
 * asks a qc lead to PASTE one is both unusable and — measured by the #85
 * locale-viewport sweep at 1440 and 412 — a `control-too-small` layout defect,
 * because 36 characters of uuid need ~307 px in a field the register sizes at
 * 224. A picker renders the SKU and the name, which is what a person knows the
 * material by, and is 40 px of text instead of 307.
 *
 * This is a cross-module READ through module 01's public API — the same door
 * the waste ledger uses for work centres — never a cross-module table access.
 * `kind` narrows it to the side of §2.2 the stage admits: a RECEIVING binding
 * names a raw material, a FINAL binding a finished product.
 */
export function useQualityBindableProducts(
  kind: "raw_material" | "finished_good",
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["quality", "bindable-products", kind],
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/catalogue/products", {
        signal,
        params: { query: { kind, status: "active", limit: 200 } },
      });
      return unwrap(res, "Could not load the catalogue").items;
    },
  });
}

export function useCancelQualityInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const res = await client.POST("/v1/quality/inspections/{id}/cancel", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { reason: input.reason },
      });
      return unwrap(res, "Could not cancel the inspection");
    },
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: qualityKeys.inspectionLists() });
      void qc.invalidateQueries({ queryKey: qualityKeys.inspection(input.id) });
    },
  });
}

// ---------------------------------------------------------------------------
// §4.6 — the Quarantine Board (07-B-3). Wire says `holds`; the user reads
// QUARANTINE (D-123 ruling 9).
// ---------------------------------------------------------------------------

export function useQuarantineList(
  query: QualityHoldsQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: qualityKeys.holds(query, cursor),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/holds", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the quarantine board");
    },
  });
}

/**
 * One disposition, one hold (§3.5c / §5). NOT a react-query mutation: the
 * board's dialog takes a BULK selection and needs the per-hold outcome —
 * including the §9.2 409 whose problem body names the winner — so the dialog
 * loops this and classifies each result itself. Throws the shared
 * `RequestError`; the caller reads `problem` off it for the §4.6
 * concurrent-disposition state.
 *
 * THE KEY IS THE CALLER'S (R1): the dialog owns the user INTENT — one key per
 * hold per intent, reused across a retry that carries the same body — and a
 * key minted per attempt here would defeat the §9.15 replay it exists for.
 * The fresh-key default stands for any caller without a retry surface.
 */
export async function dispositionQuarantine(
  id: string,
  body: QualityHoldDispositionRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<QualityHold> {
  const res = await client.POST("/v1/quality/holds/{id}/disposition", {
    params: { path: { id }, header: { "Idempotency-Key": idempotencyKey } },
    body,
  });
  return unwrap(res, "Could not disposition the quarantine");
}

/** What the §4.3 chip walk came back with: the join map, whether the five-page
 * bound bit (R5 — the screen SAYS so instead of a fourth silent meaning of an
 * absent chip), and how many records the walk covered (the N of the bounded
 * sentence). */
export interface ActiveQuarantineJoin {
  readonly byInspection: ReadonlyMap<string, QualityHold>;
  readonly truncated: boolean;
  readonly scanned: number;
}

/**
 * §4.3's failed-tab money chips: the ACTIVE quarantine records, keyed by the
 * inspection that placed them. The inspection wire carries no money — the
 * value at risk lives on the §2.7 record — so the failed tab joins
 * client-side through the one API (doctrine 3), a read the caller only
 * issues for principals holding `quality.hold.read` (without it the whole
 * column is ABSENT, matching the server's redaction-by-absence).
 *
 * BOUNDED WALK, and the bound is stated: up to five pages of 200, walked
 * WITH the queue's own facility filter (R5 — an unfiltered walk would burn
 * its pages on other facilities' holds). An active quarantine population
 * past 1000 leaves older holds unchipped — `truncated` is how the screen
 * says so — and the board is the authoritative money screen. `staleTime`
 * (R6) keeps a focus/tab-switch from re-running a five-request walk whose
 * answer seconds ago is still the answer.
 */
export function useActiveQuarantineByInspection(enabled: boolean, facilityId?: string) {
  return useQuery({
    queryKey: [...qualityKeys.holdLists(), "active-by-inspection", facilityId ?? null] as const,
    enabled,
    staleTime: 30_000,
    queryFn: async ({ signal }): Promise<ActiveQuarantineJoin> => {
      const byInspection = new Map<string, QualityHold>();
      let cursor: string | undefined;
      let truncated = false;
      let scanned = 0;
      for (let page = 0; page < 5; page++) {
        const res = unwrap(
          await client.GET("/v1/quality/holds", {
            signal,
            params: {
              query: {
                status: "active" as const,
                limit: 200,
                ...(facilityId !== undefined ? { facility_id: facilityId } : {}),
                ...(cursor !== undefined ? { cursor } : {}),
              },
            },
          }),
          "Could not load the quarantine records",
        );
        scanned += res.items.length;
        for (const hold of res.items) {
          if (hold.inspection_id !== null) byInspection.set(hold.inspection_id, hold);
        }
        if (res.next_cursor === null) break;
        cursor = res.next_cursor;
        // Five pages consumed and the server still holds more: the bound bit.
        if (page === 4) truncated = true;
      }
      return { byInspection, truncated, scanned };
    },
  });
}

/**
 * The board's product filter options. §5's `product_id` filter resolves the
 * HELD SUBJECT's product server-side (a D-129 read-model column), and a held
 * subject can be a raw-material lot or a finished-good unit alike — so this
 * picker deliberately takes the catalogue WITHOUT the `kind` narrowing the
 * §4.2 binding pickers apply.
 */
export function useQualityFilterProducts(enabled: boolean) {
  return useQuery({
    queryKey: qualityKeys.filterProducts(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/catalogue/products", {
        signal,
        params: { query: { status: "active", limit: 200 } },
      });
      return unwrap(res, "Could not load the catalogue").items;
    },
  });
}

// ---------------------------------------------------------------------------
// §4.7 — the CoA manager (07-E: templates §2.10, documents §2.11, §3.6)
// ---------------------------------------------------------------------------

export type QualityCoaTemplate = S["QualityCoaTemplate"];
export type QualityCoaDocument = S["QualityCoaDocument"];
export type QualityCoaGap = S["QualityCoaGap"];
export type QualityCoaTemplateUpsertRequest = S["QualityCoaTemplateUpsertRequest"];
export type QualityCoaDraftRequest = S["QualityCoaDraftRequest"];

export function useCoaTemplates(enabled = true) {
  return useQuery({
    queryKey: qualityKeys.coaTemplates(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/coa-templates", { signal });
      return unwrap(res, "Could not load the certificate templates").templates;
    },
  });
}

export function useUpsertCoaTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QualityCoaTemplateUpsertRequest) => {
      const res = await client.POST("/v1/quality/coa-templates", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not save the template");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qualityKeys.coaTemplates() }),
  });
}

export function useCoaDocuments(query: CoaDocumentsQuery, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.coaDocuments(query),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/coa-documents", {
        signal,
        params: {
          query: {
            ...(query.status !== undefined ? { status: query.status } : {}),
            ...(query.lotId !== undefined ? { lot_id: query.lotId } : {}),
          },
        },
      });
      return unwrap(res, "Could not load the certificates").items;
    },
  });
}

/** §3.6 steps 1–3 — the draft answers WITH its gap report, so the composer
 * can render the S11 remedy list without a second read. */
export function useDraftCoa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QualityCoaDraftRequest) => {
      const res = await client.POST("/v1/quality/coa-documents", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not draft the certificate");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qualityKeys.coaDocumentLists() }),
  });
}

export function useIssueCoa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/quality/coa-documents/{id}/issue", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not issue the certificate");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qualityKeys.coaDocumentLists() }),
  });
}

export function useRevokeCoa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string; reissue: boolean }) => {
      const res = await client.POST("/v1/quality/coa-documents/{id}/revoke", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { reason: input.reason, reissue: input.reissue },
      });
      return unwrap(res, "Could not revoke the certificate");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qualityKeys.coaDocumentLists() }),
  });
}

// ---------------------------------------------------------------------------
// §4.8 — the analytics reads (07-E; one code, per-route §5 cells server-side)
// ---------------------------------------------------------------------------

export type QualityFpyResponse = S["QualityFpyResponse"];
export type QualityParetoCause = S["QualityParetoCause"];
export type QualityShiftScoreboardRow = S["QualityShiftScoreboardRow"];
export type QualitySupplierReceivingRow = S["QualitySupplierReceivingRow"];

export function useQualityFpy(
  q: {
    from: string;
    to: string;
    groupBy: "day" | "product" | "work_center" | "shift";
    facilityId?: string;
  },
  enabled = true,
) {
  return useQuery({
    queryKey: qualityKeys.analyticsFpy(q),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/analytics/fpy", {
        signal,
        params: {
          query: {
            from: q.from,
            to: q.to,
            group_by: q.groupBy,
            ...(q.facilityId !== undefined ? { facility_id: q.facilityId } : {}),
          },
        },
      });
      return unwrap(res, "Could not load the FPY series");
    },
  });
}

export function useQualityPareto(
  q: { from: string; to: string; facilityId?: string; by?: "count" | "value" },
  enabled = true,
) {
  return useQuery({
    queryKey: qualityKeys.analyticsPareto(q),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/analytics/failure-pareto", {
        signal,
        params: {
          query: {
            from: q.from,
            to: q.to,
            ...(q.facilityId !== undefined ? { facility_id: q.facilityId } : {}),
            ...(q.by !== undefined ? { by: q.by } : {}),
          },
        },
      });
      return unwrap(res, "Could not load the failure Pareto").causes;
    },
  });
}

export function useQualityScoreboard(q: { date: string; facilityId: string }, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.analyticsScoreboard(q),
    enabled: enabled && q.facilityId !== "",
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/analytics/shift-scoreboard", {
        signal,
        params: { query: { date: q.date, facility_id: q.facilityId } },
      });
      return unwrap(res, "Could not load the shift scoreboard").rows;
    },
  });
}

export function useQualitySuppliers(q: { from: string; to: string }, enabled = true) {
  return useQuery({
    queryKey: qualityKeys.analyticsSuppliers(q),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/quality/analytics/supplier-receiving", {
        signal,
        params: { query: { from: q.from, to: q.to } },
      });
      return unwrap(res, "Could not load the supplier panel").suppliers;
    },
  });
}

/** D-216-B's admin door — group_admin only (the screen offers it to nobody
 * else; the server enforces the same cell). */
export function useRefreshAnalytics() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await client.POST("/v1/quality/analytics/refresh", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not refresh the projection");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["quality", "analytics"] }),
  });
}
